import type { GuestProjectDto } from '@/core/api/models/guestProjects';
import { resolveBrochureUrlsFromAssets } from '@/core/utils/brochureDownload';
import type {
  GuestPropertyCardModel,
  GuestPropertyTypeTag,
  GuestUnitDetailsModel,
} from '../types/guest.types';

function mapTypeTags(
  project: GuestProjectDto,
): ReadonlyArray<GuestPropertyTypeTag> {
  const units = project.units ?? [];
  const tags: GuestPropertyTypeTag[] = [];
  const seen = new Set<string>();

  for (const unit of units) {
    const label = unit.unit_type?.trim();
    if (!label) {
      continue;
    }
    const id = label.toLowerCase().replace(/\s+/g, '-');
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    tags.push({ id, label });
  }

  return tags;
}

/**
 * Place projects by Contentful `order` as a 1-based list index.
 * - `order: 1` → first slot, `order: 2` → second, …
 * - If `order` is past the list length (e.g. 100 with 20 items) → last slot
 * - If the target slot is free, take it; if taken, next free slot after it
 * - Projects without `order` fill remaining slots in original API order
 */
export function sortProjectsByContentfulOrder<
  T extends { readonly project_id: string },
>(
  projects: ReadonlyArray<T>,
  orderByProjectId: Readonly<Record<string, number>>,
): T[] {
  const total = projects.length;
  if (total === 0) {
    return [];
  }

  const slots: Array<T | undefined> = Array.from({ length: total });
  const withOrder: { project: T; order: number; index: number }[] = [];
  const withoutOrder: T[] = [];

  projects.forEach((project, index) => {
    const order = orderByProjectId[project.project_id];
    if (typeof order === 'number' && Number.isFinite(order)) {
      withOrder.push({ project, order, index });
      return;
    }
    withoutOrder.push(project);
  });

  // Lower desired index first; same target → keep API order
  withOrder.sort((a, b) => {
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    return a.index - b.index;
  });

  const findFreeSlot = (startZeroBased: number): number => {
    for (let offset = 0; offset < total; offset += 1) {
      const candidate = (startZeroBased + offset) % total;
      if (slots[candidate] === undefined) {
        return candidate;
      }
    }
    return -1;
  };

  for (const entry of withOrder) {
    // 1-based order → clamped slot in [0, total - 1]
    const desiredOneBased = Math.floor(entry.order);
    const clampedOneBased = Math.min(Math.max(desiredOneBased, 1), total);
    const desiredZeroBased = clampedOneBased - 1;
    const slot =
      slots[desiredZeroBased] === undefined
        ? desiredZeroBased
        : findFreeSlot(desiredZeroBased);
    if (slot >= 0) {
      slots[slot] = entry.project;
    }
  }

  let unorderedIndex = 0;
  for (let i = 0; i < total; i += 1) {
    if (slots[i] === undefined) {
      slots[i] = withoutOrder[unorderedIndex];
      unorderedIndex += 1;
    }
  }

  return slots.filter((project): project is T => project !== undefined);
}

function sumAvailableUnits(project: GuestProjectDto): number {
  const units = project.units ?? [];
  let total = 0;
  for (const unit of units) {
    if (typeof unit.unit_count === 'number' && Number.isFinite(unit.unit_count)) {
      total += Math.max(0, unit.unit_count);
    }
  }
  return total;
}

/** True when both coordinates are finite numbers from the API. */
export function hasGuestProjectCoordinates(
  project: Pick<GuestProjectDto, 'latitude' | 'longitude'>,
): boolean {
  return (
    typeof project.latitude === 'number' &&
    Number.isFinite(project.latitude) &&
    typeof project.longitude === 'number' &&
    Number.isFinite(project.longitude)
  );
}

function resolveSalesforceProjectTitle(
  project: Pick<GuestProjectDto, 'project_name' | 'project_name_ar'>,
  locale: 'en' | 'ar' = 'en',
): string {
  if (locale === 'ar') {
    const arabic = project.project_name_ar?.trim();
    if (arabic) {
      return arabic;
    }
  }
  return project.project_name?.trim() || '';
}

/** Contentful locale name when present; otherwise Salesforce / Boomi title. */
export function resolveGuestProjectTitle(
  salesforceName: string | undefined,
  contentfulName?: string,
): string {
  const fromContentful = contentfulName?.trim();
  if (fromContentful) {
    return fromContentful;
  }
  return salesforceName?.trim() || '';
}

function resolveSalesforceProjectDescription(
  project: Pick<GuestProjectDto, 'description' | 'description_ar'>,
  locale: 'en' | 'ar' = 'en',
): string | undefined {
  if (locale === 'ar') {
    const arabic = project.description_ar?.trim();
    if (arabic) {
      return arabic;
    }
  }
  const fallback = project.description?.trim();
  return fallback || undefined;
}

/** Contentful locale description when present; otherwise Salesforce / Boomi. */
export function resolveGuestProjectDescription(
  salesforceDescription: string | undefined,
  contentfulDescription?: string,
): string | undefined {
  const fromContentful = contentfulDescription?.trim();
  if (fromContentful) {
    return fromContentful;
  }
  const fromSalesforce = salesforceDescription?.trim();
  return fromSalesforce || undefined;
}

/** True when Contentful resolved at least one image URL for this project. */
export function guestProjectHasContentfulImages(
  images: ReadonlyArray<{ readonly uri: string }> | undefined,
): boolean {
  return Boolean(images && images.length > 0);
}

/**
 * Guest listing filter. When `showWithImagesOnly` is false, the catalogue is unchanged.
 */
export function filterGuestProjectsForListing<
  T extends { readonly project_id: string },
>(
  projects: ReadonlyArray<T>,
  imagesByProjectId: Readonly<
    Record<string, ReadonlyArray<{ readonly uri: string }> | undefined>
  >,
  showWithImagesOnly: boolean,
): ReadonlyArray<T> {
  if (!showWithImagesOnly) {
    return projects;
  }
  return projects.filter((project) =>
    guestProjectHasContentfulImages(imagesByProjectId[project.project_id]),
  );
}

export function mapProjectToCard(
  project: GuestProjectDto,
  unitsBadgeLabel: (count: number) => string,
  images: ReadonlyArray<{ readonly uri: string }> = [],
  options?: Readonly<{
    contentfulName?: string | undefined;
    locale?: 'en' | 'ar';
  }>,
): GuestPropertyCardModel {
  const totalUnits = sumAvailableUnits(project);
  const locale = options?.locale ?? 'en';
  return {
    id: project.project_id,
    title: resolveGuestProjectTitle(
      resolveSalesforceProjectTitle(project, locale),
      options?.contentfulName,
    ),
    location: project.location?.trim() || '',
    images,
    ...(totalUnits > 0 ? { unitsBadge: unitsBadgeLabel(totalUnits) } : {}),
    typeTags: mapTypeTags(project),
  };
}

export function mapProjectToDetails(
  project: GuestProjectDto,
  images: ReadonlyArray<{ readonly uri: string }> = [],
  options?: Readonly<{
    brochureUrl?: string | undefined;
    brochureUrls?: ReadonlyArray<string> | undefined;
    brochureTitle?: string | undefined;
    contentfulName?: string | undefined;
    contentfulDescription?: string | undefined;
    locale?: 'en' | 'ar';
  }>,
): GuestUnitDetailsModel {
  const totalUnits = sumAvailableUnits(project);
  const withCoords = hasGuestProjectCoordinates(project);
  const brochureUrls = resolveBrochureUrlsFromAssets({
    brochureUrls: options?.brochureUrls,
    brochureUrl: options?.brochureUrl,
  });
  const brochureTitle = options?.brochureTitle?.trim();
  const primaryUrl = brochureUrls[0];
  const locale = options?.locale ?? 'en';
  const description = resolveGuestProjectDescription(
    resolveSalesforceProjectDescription(project, locale),
    options?.contentfulDescription,
  );

  return {
    id: project.project_id,
    title: resolveGuestProjectTitle(
      resolveSalesforceProjectTitle(project, locale),
      options?.contentfulName,
    ),
    location: project.location?.trim() || '',
    ...(withCoords
      ? { latitude: project.latitude, longitude: project.longitude }
      : {}),
    ...(description ? { description } : {}),
    images,
    ...(totalUnits > 0 ? { unitsCount: totalUnits } : {}),
    typeTags: mapTypeTags(project),
    amenities: project.amenities ?? [],
    ...(primaryUrl && brochureTitle
      ? {
          documents: [
            {
              id: 'contentful-brochure',
              title: brochureTitle,
              url: primaryUrl,
              urls: brochureUrls,
            },
          ],
        }
      : {}),
  };
}
